import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, Ban, CheckCircle, Mail, Trash2, X } from 'lucide-react';
import type { User, RegisteredUser, InvitedUser } from '../../features/users/types';
import {
  updateUserRole,
  updateUserStatus,
  deleteUser,
  resendInvitation,
  cancelInvitation,
} from '../../features/users/api';
import styles from './UserActionsMenu.module.css';
import { ThreeDotsIcon } from '../common/selectIcons';

type Props = {
  user: User;
  currentUserUid?: string;
  onActionComplete: () => void;
};

type MenuPosition = {
  top: number;
  left: number;
};

export function UserActionsMenu({ user, currentUserUid, onActionComplete }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isRegisteredUser = (u: User): u is RegisteredUser => {
    return u.type === 'registered';
  };

  const isInvitedUser = (u: User): u is InvitedUser => {
    return u.type === 'invitation';
  };

  // メニュー位置を計算
  const calculateMenuPosition = (): MenuPosition => {
    if (!triggerRef.current) {
      return { top: 0, left: 0 };
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 200; // min-width from CSS

    // メニューを右揃えで表示
    return {
      top: rect.bottom + 4, // 4px margin
      left: rect.right - menuWidth,
    };
  };

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setMenuPosition(calculateMenuPosition());
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      setMenuPosition(calculateMenuPosition());
    }
    setIsOpen(!isOpen);
  };

  const handleAction = async (action: () => Promise<unknown>) => {
    setIsLoading(true);
    try {
      await action();
      onActionComplete();
      setIsOpen(false);
    } catch (err: unknown) {
      console.error('Action failed:', err);
      const errorMessage = (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { error?: string } } }).response?.data?.error === 'string'
      )
        ? (err as { response?: { data?: { error?: string } } }).response!.data!.error!
        : (
          err instanceof Error
            ? err.message
            : 'アクションに失敗しました'
        );
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 招待中ユーザーのアクション
  if (isInvitedUser(user)) {
    return (
      <div className={styles.container}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          onClick={handleToggle}
          disabled={isLoading}
        >
          <ThreeDotsIcon vertical />
        </button>
        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              className={`modal-surface ${styles.menu}`}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => handleAction(() => resendInvitation(user.invitation_id))}
                disabled={isLoading}
              >
                <Mail size={14} aria-hidden="true" />
                <span>再送信</span>
              </button>
              <button
                type="button"
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={() => {
                  if (confirm(`${user.email} への招待を取り消しますか？`)) {
                    handleAction(() => cancelInvitation(user.invitation_id));
                  }
                }}
                disabled={isLoading}
              >
                <X size={14} aria-hidden="true" />
                <span>取り消し</span>
              </button>
            </div>,
            document.body
          )}
      </div>
    );
  }

  // 登録済みユーザーのアクション
  if (isRegisteredUser(user)) {
    // 論理削除済みユーザーは email/auth が匿名化されており、アクティブ化やロール変更で
    // 復活させることはできない (本人が再 signup する必要がある)。ゾンビ行に対する
    // 紛らわしいアクションを隠し、メニュー自体を出さない。
    const isDeletedUser = /^deleted\+.+@deleted\.local$/.test(user.email || '');
    if (isDeletedUser) {
      return null;
    }

    const isSelf = !!currentUserUid && user.user_uid === currentUserUid;
    const deleteLabel = isSelf ? 'この組織から抜ける' : '削除';
    const deleteConfirmMessage = isSelf
      ? `${user.email} でこの組織から抜けますか？この操作は取り消せません。`
      : `${user.email} を削除しますか？この操作は取り消せません。`;

    return (
      <div className={styles.container}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          onClick={handleToggle}
          disabled={isLoading}
        >
          <ThreeDotsIcon vertical />
        </button>
        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              className={`modal-surface ${styles.menu}`}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
              }}
            >
              {/* ロール変更 */}
              {user.role === 'user' ? (
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (confirm(`${user.email} を管理者に変更しますか？`)) {
                      handleAction(() => updateUserRole(user.id, 'admin'));
                    }
                  }}
                  disabled={isLoading}
                >
                  <ArrowUp size={14} aria-hidden="true" />
                  <span>管理者に変更</span>
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (confirm(`${user.email} をユーザーに変更しますか？`)) {
                      handleAction(() => updateUserRole(user.id, 'user'));
                    }
                  }}
                  disabled={isLoading}
                >
                  <ArrowDown size={14} aria-hidden="true" />
                  <span>ユーザーに変更</span>
                </button>
              )}

              {/* アクティブ/非アクティブ切り替え */}
              {user.status === 'active' ? (
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (confirm(`${user.email} を非アクティブにしますか？`)) {
                      handleAction(() => updateUserStatus(user.id, false));
                    }
                  }}
                  disabled={isLoading}
                >
                  <Ban size={14} aria-hidden="true" />
                  <span>非アクティブ化</span>
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (confirm(`${user.email} をアクティブにしますか？`)) {
                      handleAction(() => updateUserStatus(user.id, true));
                    }
                  }}
                  disabled={isLoading}
                >
                  <CheckCircle size={14} aria-hidden="true" />
                  <span>アクティブ化</span>
                </button>
              )}

              {/* 削除 */}
              <button
                type="button"
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={() => {
                  if (confirm(deleteConfirmMessage)) {
                    handleAction(() => deleteUser(user.id));
                  }
                }}
                disabled={isLoading}
              >
                <Trash2 size={14} aria-hidden="true" />
                <span>{deleteLabel}</span>
              </button>
            </div>,
            document.body
          )}
      </div>
    );
  }

  return null;
}
